import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GrantsAndAllowedApis,
  Language,
  OutputNodeDefinition,
  Provider,
  QueryAndOutputs,
  TableSchema,
  TokenAllowedApis,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentGetRequiredGrantsParams,
  WorkflowComponentParameterMode,
  WorkflowComponentParamType,
  WorkflowComponentValue
} from '../types'
import { isExpressionValue } from '../utils'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class CreateBuilderMap extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.createbuildermap',
        group: 'Input/Output',
        title: 'Create Builder Map',
        description: `
          This component creates a Builder map on its first execution. On subsequent executions, it can update, overwrite or create a copy of the existing map.
        `,
        starred: true,
        warningTitle: 'Create copy mode',
        warningDescription:
          'Please note that automating this workflow via API or scheduling it may lead to the creation of a large number of maps.',
        warningShowIf: [{ parameter: 'mode', value: 'copy' }]
      },
      [
        {
          name: 'sources',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source tables',
          mode: WorkflowComponentParameterMode.Multiple
        },
        {
          name: 'mapName',
          type: WorkflowComponentParamType.String,
          title: 'Map name',
          placeholder: 'workflows.parameterForm.createBuilderMapPlaceholder'
        },
        {
          name: 'mode',
          type: WorkflowComponentParamType.Selection,
          mode: WorkflowComponentParameterMode.Radio,
          title: 'Re-running mode',
          helper: 'This setting controls the behavior after the first execution',
          options: [
            {
              value: 'update',
              name: 'Update',
              description: 'Updates data sources while keeping map styles, widgets and other settings.',
              icon: 'update'
            },
            {
              value: 'overwrite',
              name: 'Overwrite',
              description: 'Deletes the current map and creates a new one with default settings.',
              icon: 'autorenew'
            },
            {
              value: 'copy',
              name: 'Create copy',
              description: 'Creates a new copy of the map with same styles, sharing, privacy and other settings.',
              icon: 'mapcopy'
            }
          ],
          value: 'update',
          default: 'update',
          allowExpressions: false
        }
      ]
    )
  }

  /**
   * Generates a SQL command to the __CREATE_BUILDER_MAP procedure for the selected
   * provider.

   * @param {WorkflowComponentSQLParams} params - The parameters needed to generate the SQL command.
   * @returns {Promise<QueryAndOutputs>} A promise that resolves to the SQL query and output settings.
   */
  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const isDryRun = codeType === WorkflowCodeType.DryRun
    const isStoredProcedure = codeType === WorkflowCodeType.StoredProcedure
    const preparedParams = this._prepareParameters(params)
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    const quotedTablename = connection.quoteName(tablename)
    const tempLocation = connection.quoteName(connection.tempLocation)
    const outputTable = !tablename.includes('.') ? `${tempLocation}.${tablename}` : tablename
    const quotedOutputTable = connection.quoteName(outputTable)
    const createOutputTableQuery = dedent`
      ${connection.executeDynamicSQL({
        sql: connection.getCreateTempTableQuery(quotedTablename, `SELECT * FROM ${quotedOutputTable}`, codeType, null),
        addQuotes: true
      })}
    `

    let query = ''
    if (!isDryRun) {
      const analyticsToolbox = connection.quoteName(envVars.analyticsToolboxDataset as string)
      const preparedSources = this._prepareSources(preparedParams, workflow, connection, componentName, codeType)
      const datasets = preparedSources.map((preparedSource) => {
        return {
          source: connection.applyCapitalization(preparedSource.source),
          sourceWorkflowNodeId: preparedSource.sourceWorkflowNodeId
        }
      })
      const variableReferencer = connection.type === Provider.Snowflake ? ':' : ''
      let stringifiedDatasets = JSON.stringify(datasets)
      for (const preparedSource of preparedSources) {
        if (preparedSource.variableDefinition) {
          stringifiedDatasets = stringifiedDatasets.replace(
            connection.applyCapitalization(preparedSource.source),
            `' || ${variableReferencer}${connection.applyCapitalization(preparedSource.source)} || '`
          )
        }
      }
      const mapNameParam = (workflow.componentValues[componentName]?.mapName as WorkflowComponentValue)?.value as string

      let mapName
      if (isExpressionValue(mapNameParam)) {
        mapName = preparedParams.mapName as string
      } else {
        mapName = `'${preparedParams.mapName}'`
      }

      const writeToTempTables = preparedSources
        .map((preparedSource) =>
          preparedSource.sourceTemp
            ? connection.executeDynamicSQL({
                sql: connection.getCreateTempTableQuery(
                  `${preparedSource.source}`,
                  `SELECT * FROM ${connection.quoteName(preparedSource.sourceTemp)}`,
                  WorkflowCodeType.Full,
                  null
                ),
                addQuotes: true
              })
            : ''
        )
        .filter(Boolean)
        .join('\n')

      const dropOutputTable = connection.getDropTableQuery(quotedOutputTable)

      switch (connection.type) {
        case Provider.BigQuery: {
          query = dedent`
            BEGIN
            ${preparedSources
              .filter((source) => source.variableDefinition)
              .map((source) => {
                return `DECLARE ${connection.applyCapitalization(source.source)} STRING DEFAULT (${
                  source.variableDefinition
                });`
              })
              .join('\n')}
            ${writeToTempTables}
            ${dropOutputTable}
            CALL ${analyticsToolbox}.__CREATE_BUILDER_MAP(
              '${envVars.apiBaseUrl}',
              '${envVars.accessToken}',
              '${connection.name}',
              ${mapName},
              '${preparedParams.mode}',
              '${workflow.id}',
              '${componentName}',
              '${stringifiedDatasets}',
              '${envVars.domain}',
              '${outputTable}',
              NULL
            );
            END;
            ${isStoredProcedure ? createOutputTableQuery : ''}
          `.replace(/(^[ \t]*\n)/gm, '')
          break
        }
        case Provider.Snowflake:
          query = dedent`
            ${preparedSources.some((source) => source.variableDefinition) ? 'DECLARE' : ''}
            ${preparedSources
              .filter((source) => source.variableDefinition)
              .map((source) => {
                return `${connection.applyCapitalization(source.source)} STRING DEFAULT (${source.variableDefinition});`
              })
              .join('\n')}
            BEGIN
            ${dropOutputTable}
            CALL ${analyticsToolbox}._CREATE_BUILDER_MAP(
              '${connection.name}',
              ${mapName},
              '${preparedParams.mode}',
              '${workflow.id}',
              '${componentName}',
              '${stringifiedDatasets}',
              '${envVars.domain}',
              '${outputTable}',
              NULL,
              '${envVars.apiBaseUrl}',
              '${envVars.accessToken}'
            );
            END;
          `.replace(/(^[ \t]*\n)/gm, '')
          break
        case Provider.Redshift:
          query = dedent`
            ${preparedSources.some((source) => source.variableDefinition) ? 'DECLARE' : ''}
            ${preparedSources
              .filter((source) => source.variableDefinition)
              .map((source) => {
                return `${connection.applyCapitalization(source.source)} VARCHAR(MAX) DEFAULT (${
                  source.variableDefinition
                });`
              })
              .join('\n')}
            BEGIN
            ${writeToTempTables}
            COMMIT;
            ${dropOutputTable}
            CALL ${analyticsToolbox}.__CREATE_BUILDER_MAP(
              '${connection.name}',
              ${mapName},
              '${preparedParams.mode}',
              '${workflow.id}',
              '${componentName}',
              '${stringifiedDatasets}',
              '${envVars.domain}',
              '${outputTable}',
              NULL,
              '${envVars.apiBaseUrl}',
              '${envVars.accessToken}'
            );
            ${isStoredProcedure ? createOutputTableQuery : ''}
            END;
          `.replace(/(^[ \t]*\n)/gm, '')
          break
        case Provider.DatabricksRest: {
          const dropTableQuery = dedent`
            DROP TABLE IF EXISTS ${quotedTablename};
          `
          query = dedent`
            ${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(dropTableQuery)}

            import requests

            # Set up headers
            headers = {
                "Authorization": f"Bearer ${envVars.accessToken}",
                "Content-Type": "application/json"
            }

            # Define endpoints
            resources_endpoint = f"${envVars.apiBaseUrl}/v3/connections/${connection.name}/resources"
            map_endpoint = f"${envVars.apiBaseUrl}/v3/workflows/${workflow.id}/map"

            # Get enriched datasets
            enriched_datasets = []
            for dataset in ${JSON.stringify(datasets)}:
                try:
                    dataset_resource_endpoint = f"{resources_endpoint}/{dataset['source']}"
                    response = requests.get(dataset_resource_endpoint, headers=headers)
                    result = response.json()
                    if 'geomField' in result:
                        dataset['geoColumn'] = result['geomField']
                    t = result.get('type')
                    normalized = t.lower() if isinstance(t, str) else None
                    if normalized == 'raster':
                        dataset['type'] = 'raster'
                    elif normalized == 'tileset':
                        dataset['type'] = 'tileset'
                    elif normalized:
                        dataset['type'] = 'table'
                    enriched_datasets.append(dataset)
                except:
                    enriched_datasets.append(dataset)

            # Prepare the payload
            payload = {
                "mapName": ${mapName.replace(/\((.*?)\)/, '$1')},
                "mode": "${preparedParams.mode}",
                "workflowNodeId": "${componentName}",
                "datasets": enriched_datasets
            }

            try:
                # Make the request to create map
                response = requests.post(map_endpoint, json=payload, headers=headers)
                response.raise_for_status()
                result = response.json()

                # Create output table with results
                if "mapId" in result and "updatedAt" in result:
                    map_id = result["mapId"]
                    map_url = f"https://${envVars.domain}/builder/{map_id}"
                    updated_at = result["updatedAt"]

                    spark.sql(f"""
                        CREATE OR REPLACE TABLE ${quotedTablename} AS
                        SELECT
                            '{map_id}' as map_id,
                            '{map_url}' as map_url,
                            TIMESTAMP('{updated_at}') as updated_at
                    """)
                else:
                    raise RuntimeError(f"Invalid API response format. Response: {result}")
            except requests.exceptions.HTTPError as e:
                raise RuntimeError(f"API request failed: {str(e)} - Response: {e.response.text}")
            except requests.exceptions.RequestException as e:
                raise RuntimeError(f"Request failed: {str(e)}")
            except Exception as e:
                raise RuntimeError(f"Error creating builder map: {str(e)}")
            `
          break
        }
        default:
          return null
      }
    }

    return {
      outputs: {
        [SINGLE_OUTPUT_NAME]: tablename
      },
      query
    }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  getRequiredGrants({
    workflow,
    connection,
    componentName
  }: WorkflowComponentGetRequiredGrantsParams): GrantsAndAllowedApis {
    // Sources in Full or StoredProcedure modes are different,
    // so we need to build the required grants for each mode:
    // - Full mode source: <database>.<schema>.<table>
    // - StoredProcedure mode source: <database>.<schema>.<table> OR <temp_table>
    const requiredGrantsFull = this._buildRequiredGrants({
      workflow,
      connection,
      componentName,
      codeType: WorkflowCodeType.Full
    })
    const requiredGrantsStoredProcedure = this._buildRequiredGrants({
      workflow,
      connection,
      componentName,
      codeType: WorkflowCodeType.StoredProcedure
    })
    return {
      grants: this._getUniqueGrants([...requiredGrantsFull, ...requiredGrantsStoredProcedure]),
      apis: [TokenAllowedApis.workflows, TokenAllowedApis.resources]
    }
  }

  _buildRequiredGrants({ workflow, connection, componentName, codeType }) {
    const componentParams = workflow.resolveParameterValues({
      connection,
      codeType
    })
    const preparedParams = this._prepareParameters(componentParams[componentName])
    const preparedSources = this._prepareSources(preparedParams, workflow, connection, componentName, codeType)
    return preparedSources.map((preparedSource) => ({
      connection_name: connection.name,
      resource: preparedSource.source
    }))
  }

  _getUniqueGrants(grants: { connection_name: string; resource: string }[]) {
    const uniqueGrants = new Map<string, { connection_name: string; resource: string }>()

    grants.forEach((grant) => {
      const key = `${grant.connection_name}:${grant.resource}`
      uniqueGrants.set(key, grant)
    })

    return Array.from(uniqueGrants.values())
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema(): TableSchema {
    return [
      { name: 'map_id', type: ColumnDataType.String },
      { name: 'map_url', type: ColumnDataType.String },
      { name: 'updated_at', type: ColumnDataType.Timestamp }
    ]
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.BigQuery:
        return '1.21.0'
      case Provider.Snowflake:
        return '1.19.0'
      case Provider.Redshift:
        return '1.10.0'
    }
    return null
  }

  getVersion(provider: Provider) {
    return '1'
  }

  sourceFieldPerComponent = {
    'native.saveastable': 'destination',
    'native.gettablebyname': 'source'
  }

  _prepareSources(preparedParams, workflow, connection, componentName, codeType) {
    const inputSources = workflow.componentValues[componentName].sources as WorkflowComponentValue[]
    return (preparedParams.sources as string[]).map((source, index) => {
      const inputSourceName = (inputSources?.[index]?.value as OutputNodeDefinition).component
      let variableDefinition
      const name = workflow.components[inputSourceName]?.metadata?.name
      if (name && name in this.sourceFieldPerComponent) {
        const field = this.sourceFieldPerComponent[name as keyof typeof this.sourceFieldPerComponent]
        source = (workflow.componentValues[inputSourceName]?.[field] as WorkflowComponentValue)?.value as string
        if (isExpressionValue(source)) {
          if (codeType != WorkflowCodeType.StoredProcedure) {
            // The destination name for the saved table can be an SQL expression,
            // which can not be computed in JS. Therefore, we only support simple
            // concat expressions. For example: {{'a' || 'b'}} -> ab
            source = connection.replaceVariablesInScriptQuery(source, workflow.variables)
            source = source.replace(/[{}|\s']/g, '')
          } else {
            source = connection.replaceVariablesInStoredProcedureQuery(workflow.variables, source, false)
            source = source.replace(/[{}|\s']/g, '')
            variableDefinition = source
            source = `__dataSource${index}`
          }
        }
      }
      const isSourceTemp = !variableDefinition && !source.includes('.') // source not fully qualified
      return {
        source: isSourceTemp ? `${connection.tempLocation}.${source}` : source,
        sourceTemp: isSourceTemp ? source : undefined,
        sourceWorkflowNodeId: inputSourceName,
        variableDefinition: variableDefinition
      }
    })
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Redshift, Provider.DatabricksRest]
  }
}

export { CreateBuilderMap }
